import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MoreVertical } from 'lucide-react';
import WorkflowUtilityPanel from './WorkflowUtilityPanel';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

function formatVersionDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function VersionCard({ version, current, onRestore }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const status = version.status === 'active' ? 'Published' : 'Draft';
  const initials = (version.author_initials || version.author_name || 'U').slice(0, 1).toUpperCase();

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        current ? 'border-sky-300 bg-sky-50/50' : 'border-slate-200',
      )}
      data-testid={`version-card-${version.version_number}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-900">{version.name}</p>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
              v{version.version_number}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold text-amber-900">
              {initials}
            </span>
            {formatVersionDate(version.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
              status === 'Published'
                ? 'bg-emerald-100 text-emerald-700'
                : 'border border-slate-300 text-slate-600',
            )}
          >
            {status}
          </span>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Version actions"
          >
            <MoreVertical size={14} />
          </button>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          disabled={current}
          onClick={() => onRestore(version)}
          className={cn(
            'text-sm font-medium',
            current ? 'cursor-not-allowed text-slate-300' : 'text-sky-600 hover:text-sky-700',
          )}
        >
          Restore
        </button>
        {menuOpen && (
          <div className="absolute right-8 mt-8 w-36 rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg">
            <button type="button" className="block w-full px-3 py-2 text-left hover:bg-slate-50" onClick={() => onRestore(version)}>
              Restore
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const WorkflowVersionHistoryPanel = ({ apiBase, token, workflowId, onClose, onRestored }) => {
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiBase}/${workflowId}/versions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setVersions(res.data?.versions || []);
      } catch {
        if (!cancelled) setVersions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token && workflowId) load();
    return () => {
      cancelled = true;
    };
  }, [apiBase, token, workflowId]);

  const current = versions.find((v) => v.is_current) || versions[0];
  const previous = versions.filter((v) => !v.is_current);

  const handleRestore = async (version) => {
    if (!window.confirm(`Restore version v${version.version_number}? Unsaved changes will be lost.`)) return;
    try {
      await axios.post(
        `${apiBase}/${workflowId}/versions/${version.id}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Restored version v${version.version_number}`);
      onRestored?.();
    } catch {
      toast.error('Failed to restore version');
    }
  };

  return (
    <WorkflowUtilityPanel
      title="Version history"
      subtitle="Version history is kept for 30 days, or the last 10 versions if older."
      onClose={onClose}
      testId="workflow-version-history-panel"
    >
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : versions.length === 0 ? (
        <p className="text-sm text-slate-500">No previous versions yet.</p>
      ) : (
        <div className="space-y-5">
          <section>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Current version</p>
            {current && (
              <VersionCard version={current} current onRestore={handleRestore} />
            )}
          </section>
          <section>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Previous versions</p>
            {previous.length === 0 ? (
              <p className="text-sm text-slate-500">No previous versions yet.</p>
            ) : (
              <div className="space-y-2">
                {previous.map((version) => (
                  <VersionCard key={version.id} version={version} onRestore={handleRestore} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </WorkflowUtilityPanel>
  );
};

export default WorkflowVersionHistoryPanel;
